import { useState, useEffect } from "react";
import { t } from "@/lib/i18n/messages";
import { useAppContext } from "@/providers";
import { useToast } from "@/components/ui/toast";
import { updateProfile as apiUpdateProfile } from "@/lib/api/user";
import type { PartnerProfile, UpdateProfileInput } from "@/types/user";

interface ProfileFormProps {
  profile: PartnerProfile | null;
  onSuccess: () => void;
  loading: boolean;
}

export function ProfileForm({ profile, onSuccess, loading }: ProfileFormProps) {
  const { locale } = useAppContext();
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [sector, setSector] = useState<"charity" | "government" | "private" | "">("");
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (profile) {
      setName(profile.name);
      setDescription(profile.description ?? "");
      setSector(profile.sector ?? "");
      setEmail(profile.email);
      setWebsite(profile.website ?? "");
    }
  }, [profile]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFieldErrors({});
    setSubmitting(true);
    const data: UpdateProfileInput = {
      name,
      email,
      ...(description && { description }),
      ...(sector && { sector: sector as "charity" | "government" | "private" }),
      ...(website && { website }),
    };
    try {
      const res = await apiUpdateProfile(data);
      if (res.success) {
        toast(t(locale, "profileUpdated"), "success");
        onSuccess();
      } else {
        const errors: Record<string, string> = {};
        if (res.errors) {
          Object.entries(res.errors).forEach(([k, msgs]) => {
            errors[k] = msgs[0] ?? "";
          });
        }
        setFieldErrors(errors);
        if (!Object.keys(errors).length) {
          toast(res.message ?? t(locale, "errorLoadingData"), "error");
        }
      }
    } catch {
      toast(t(locale, "errorLoadingData"), "error");
    } finally {
      setSubmitting(false);
    }
  }

  const inputClass =
    "w-full px-4 py-3 bg-muted border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all";

  return (
    <form onSubmit={handleSubmit} className="flex-1 space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium mb-2 text-foreground-secondary">
            {t(locale, "orgName")}
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={inputClass}
            required
          />
          {fieldErrors["name"] && (
            <p className="text-red-500 text-xs mt-1">{fieldErrors["name"]}</p>
          )}
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium mb-2 text-foreground-secondary">
            {t(locale, "orgDescription")}
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className={`${inputClass} resize-none`}
            rows={4}
            placeholder={t(locale, "orgDescription")}
          />
          {fieldErrors["description"] && (
            <p className="text-red-500 text-xs mt-1">{fieldErrors["description"]}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium mb-2 text-foreground-secondary">
            {t(locale, "sector")}
          </label>
          <div className="relative">
            <select
              value={sector}
              onChange={(e) => setSector(e.target.value as typeof sector)}
              className={`${inputClass} appearance-none bg-none pe-10`}
            >
              <option value="" disabled>
                {t(locale, "selectSector")}
              </option>
              <option value="charity">{t(locale, "sectorCharity")}</option>
              <option value="government">{t(locale, "sectorGovernment")}</option>
              <option value="private">{t(locale, "sectorPrivate")}</option>
            </select>
            <span className="material-icons-outlined absolute top-1/2 -translate-y-1/2 end-3 pointer-events-none text-gray-400">
              expand_more
            </span>
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium mb-2 text-foreground-secondary">
            {t(locale, "orgEmail")}
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputClass}
            required
          />
          {fieldErrors["email"] && (
            <p className="text-red-500 text-xs mt-1">{fieldErrors["email"]}</p>
          )}
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium mb-2 text-foreground-secondary">
            {t(locale, "websiteField")}
          </label>
          <input
            type="url"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            className={inputClass}
            placeholder={t(locale, "websiteField")}
          />
          {fieldErrors["website"] && (
            <p className="text-red-500 text-xs mt-1">{fieldErrors["website"]}</p>
          )}
        </div>
      </div>
      <div className="pt-10 flex justify-end">
        <button
          type="submit"
          disabled={loading || submitting}
          className="bg-primary hover:bg-primary/90 text-white px-10 py-3.5 rounded-lg font-bold shadow-lg shadow-primary/25 transition-all hover:translate-y-[-2px] active:translate-y-[0] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading || submitting ? t(locale, "loading") : t(locale, "saveChanges")}
        </button>
      </div>
    </form>
  );
}
